// src/pages/AllProjectsPage.jsx
import React, { useState } from 'react';
import projectsData from '../data/projectsData'; // Adjust path if needed

const AllProjectsPage = () => {
  const sortedProjects = [...projectsData].sort((a, b) => {
    if (!a.startDate) return 1; // Push empty dates to the end
    if (!b.startDate) return -1;
    return new Date(b.startDate) - new Date(a.startDate);
  });

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const itemsPerSlide = 6; // Show 6 projects per slide (2 rows of 3)
  const totalSlides = Math.ceil(sortedProjects.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  if (sortedProjects.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 min-h-screen">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-12">Our Projects</h1>
        <p className="text-center text-gray-600">No projects or grants available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16 min-h-screen">
      <h1 className="text-4xl font-bold text-center text-gray-800 mb-12">Our Projects & Grants</h1>
      
      {/* Projects Carousel */}
      <div className="relative max-w-7xl mx-auto">
        {/* Navigation Arrows - only show if more than 1 slide */}
        {totalSlides > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 z-10 transition-all duration-200 hover:scale-110"
              aria-label="Previous slide"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 z-10 transition-all duration-200 hover:scale-110"
              aria-label="Next slide"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Carousel Content */}
        <div className="overflow-hidden rounded-lg">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
              <div key={slideIndex} className="w-full flex-shrink-0">
                {/* Project Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {sortedProjects
                    .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                    .map((project) => (
                      <div 
                        key={project.id} 
                        className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col"
                      >
                        {project.imageUrl && (
                          <img
                            src={project.imageUrl}
                            alt={project.title}
                            className="w-full h-48 object-cover rounded-t-lg mb-4"
                          />
                        )}
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">{project.title}</h2>

                        {(project.startDate || project.endDate) && (
                          <p className="text-md text-gray-600 mb-2">
                            {project.startDate ? new Date(project.startDate).toLocaleDateString() : ""}
                            {project.endDate ? ` - ${new Date(project.endDate).toLocaleDateString()}` : ""}
                          </p>
                        )}

                        <p className="text-gray-700 mb-4 flex-grow">{project.shortDescription}</p>

                        {project.isGrant && (
                          <p className="text-sm text-purple-700 mb-1">
                            <span className="font-bold">Grant from:</span> {project.fundingAgency}
                          </p>
                        )}
                        {project.principalInvestigators?.length > 0 && (
                          <p className="text-sm text-gray-500 mb-1">
                            <span className="font-bold">PIs:</span> {project.principalInvestigators.join(', ')}
                          </p>
                        )}
                        {project.status && (
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${
                              project.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : project.status === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : project.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </span>
                        )}
                        {project.projectLink && (
                          <a
                            href={project.projectLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 text-blue-600 hover:text-blue-800 font-medium self-start"
                          >
                            Learn More &rarr;
                          </a>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Numbered Pagination - only show if more than 1 slide */}
        {totalSlides > 1 && (
          <div className="flex justify-center mt-12 space-x-3">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center font-semibold text-sm ${
                  index === currentSlide
                    ? 'bg-blue-600 text-white scale-110 shadow-lg'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-105'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}

        {/* Slide Counter - only show if more than 1 slide */}
        {totalSlides > 1 && (
          <div className="text-center mt-4">
            <span className="text-sm text-gray-500">
              {currentSlide + 1} of {totalSlides} • Showing {Math.min(itemsPerSlide, sortedProjects.length - currentSlide * itemsPerSlide)} of {sortedProjects.length} projects
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllProjectsPage;